import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../api';
import { Spinner } from '../components/UI';
import { fmtEntero, sinPrefijoDireccion } from './Dashboard';
import { fechaISO } from '../utils';

// Tablero de Dirección General, con la estética de un tablero de BI: barras de
// título en azul marino, paneles enmarcados, tablas con importe/% y renglón de
// total, y las cifras grandes arriba.
//
// No es una versión reducida del tablero de finanzas: ahí cada número está para
// explorarse (8 filtros, 30 paneles, clic para cruzar dimensiones) y aquí cada
// panel está para decidir algo, y lo lleva escrito. Si un dato no cambia una
// decisión, no entra.
//
// Todo sale del mismo /api/dashboard/analytics que alimenta al tablero de
// finanzas, así que no hay una segunda verdad que se pueda desincronizar: lo que
// cambia es la jerarquía y la forma de leerlo.

// ── Paleta ─────────────────────────────────────────────────────────────────
const MARINO   = '#111111';   // barras de título: negro de marca con texto blanco
const MARINO_2 = '#3d4148';
const TINTA    = '#111111';
const GRIS     = '#5b6068';
const BORDE    = '#dedfe3';
const FONDO_ZEBRA = 'rgba(0,0,0,0.025)';
// Rampa de un solo tono para las barras: codifican magnitud, no identidad, así
// que van del plasma encendido (mayor) al acero apagado (menor).
const RAMPA = ['#ff541c', '#d65624', '#ae5a3b', '#855d51', '#5c6168']; // naranja de marca → acero
// Colores de estado, reservados: nunca se usan como "serie 4", y siempre viajan
// con icono y etiqueta para no depender del color.
const ROJO  = '#be123c';
const AMBAR = '#b45309';
const VERDE = '#15803d';

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

const PERIODOS = [
  { id: 'mes',  label: 'Este mes' },
  { id: '3m',   label: '3 meses' },
  { id: 'anio', label: 'Este año' },
  { id: 'custom', label: 'A la medida' },
];

const iso = (d) => fechaISO(d);
const hoyISO = () => iso(new Date());

function rangoPeriodo(id, custom) {
  const hoy = new Date();
  // Rango a la medida: sólo se usa cuando están las dos fechas; si falta una, se
  // cae al mes en curso para no pedirle al backend un rango a medio armar.
  if (id === 'custom') {
    if (custom?.desde && custom?.hasta) return { desde: custom.desde, hasta: custom.hasta };
    return { desde: iso(new Date(hoy.getFullYear(), hoy.getMonth(), 1)), hasta: hoyISO() };
  }
  if (id === '3m')   return { desde: iso(new Date(hoy.getFullYear(), hoy.getMonth() - 2, 1)), hasta: hoyISO() };
  if (id === 'anio') return { desde: iso(new Date(hoy.getFullYear(), 0, 1)), hasta: hoyISO() };
  return { desde: iso(new Date(hoy.getFullYear(), hoy.getMonth(), 1)), hasta: hoyISO() };
}

// Mismo largo que el periodo elegido, inmediatamente anterior: es contra lo que
// se compara cada categoría para saber qué subió.
function rangoAnterior(id, custom) {
  const { desde, hasta } = rangoPeriodo(id, custom);
  const d = new Date(`${desde}T00:00:00`), h = new Date(`${hasta}T00:00:00`);
  const dias = Math.max(1, Math.round((h - d) / 86400000) + 1);
  return { desde: iso(new Date(d.getTime() - dias * 86400000)), hasta: iso(new Date(d.getTime() - 86400000)) };
}

// La tendencia necesita historia propia, no la del periodo elegido: en "este
// mes" no habría nada que ver.
function rangoTendencia() {
  const hoy = new Date();
  return { desde: iso(new Date(hoy.getFullYear(), hoy.getMonth() - 11, 1)), hasta: hoyISO() };
}

const pct = (parte, total) => (total > 0 ? (parte / total) * 100 : 0);
const pesos = (n) => `$${fmtEntero(n)}`;

// ── Piezas ─────────────────────────────────────────────────────────────────

// Panel con barra de título azul marino. `decision` es la pregunta que el panel
// contesta: es lo que separa un tablero que informa de uno que sirve para decidir.
function Panel({ titulo, decision, children, accion, alto }) {
  return (
    <div style={{ minWidth: 0, border: `1px solid ${BORDE}`, borderRadius: 8, overflow: 'hidden', background: 'var(--panel)', display: 'flex', flexDirection: 'column', height: alto }}>
      <div style={{ background: MARINO, padding: '7px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ color: '#fff', fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{titulo}</span>
        {accion}
      </div>
      {decision && (
        <div style={{ padding: '6px 12px', background: 'var(--well)', borderBottom: `1px solid ${BORDE}`, fontSize: 11.5, color: GRIS, fontStyle: 'italic' }}>
          {decision}
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
    </div>
  );
}

const BotonDetalle = ({ onClick }) => (
  <button onClick={onClick} title="Abrir en el tablero de finanzas con este filtro"
    style={{ padding: '2px 8px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.45)', background: 'transparent', color: '#fff', cursor: 'pointer', fontSize: 10.5, fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
    detalle →
  </button>
);

// Cifra destacada. El número va en tinta y el color vive en la barra superior:
// así se lee igual de bien sin distinguir colores.
function Cifra({ etiqueta, valor, sub, nota, acento = MARINO_2, grande }) {
  return (
    <div style={{ border: `1px solid ${BORDE}`, borderRadius: 8, overflow: 'hidden', background: 'var(--panel)' }}>
      <div style={{ height: 4, background: acento }} />
      <div style={{ padding: grande ? '12px 16px 14px' : '10px 14px 12px', textAlign: 'center' }}>
        <div style={{ fontSize: grande ? 30 : 22, fontWeight: 700, color: TINTA, fontFamily: 'Geist Mono, monospace', letterSpacing: '-0.02em', lineHeight: 1.1 }}>
          {valor}
        </div>
        <div style={{ fontSize: 10.5, fontWeight: 700, color: GRIS, textTransform: 'uppercase', letterSpacing: '0.07em', marginTop: 5 }}>{etiqueta}</div>
        {sub && <div style={{ fontSize: 11.5, color: GRIS, marginTop: 3 }}>{sub}</div>}
        {nota && <div style={{ fontSize: 11.5, color: nota.color, marginTop: 5, fontWeight: 700 }}>{nota.icono} {nota.texto}</div>}
      </div>
    </div>
  );
}

// Tabla con cebra y renglón de total, como las del tablero de referencia.
function Tabla({ columnas, filas, total }) {
  const alineado = (i) => (columnas[i].num ? 'right' : 'left');
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: 'var(--well)' }}>
            {columnas.map((c, i) => (
              <th key={c.label} style={{ padding: '6px 10px', textAlign: alineado(i), fontSize: 10.5, fontWeight: 700, color: GRIS, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1.5px solid ${BORDE}`, whiteSpace: 'nowrap' }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filas.map((f, i) => (
            <tr key={f.key ?? i} style={{ background: i % 2 ? FONDO_ZEBRA : 'transparent' }}>
              {f.celdas.map((celda, j) => (
                <td key={j} style={{ padding: '6px 10px', textAlign: alineado(j), color: TINTA,
                  fontFamily: columnas[j].num ? 'Geist Mono, monospace' : 'inherit',
                  fontWeight: columnas[j].num ? 600 : 400,
                  maxWidth: columnas[j].num ? undefined : 210, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={typeof celda === 'string' ? celda : undefined}>
                  {celda}
                </td>
              ))}
            </tr>
          ))}
          {total && (
            <tr style={{ background: 'var(--panel)', borderTop: `1.5px solid ${MARINO}` }}>
              {total.map((celda, j) => (
                <td key={j} style={{ padding: '7px 10px', textAlign: alineado(j), fontWeight: 700, color: TINTA, fontFamily: columnas[j].num ? 'Geist Mono, monospace' : 'inherit' }}>
                  {celda}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// Barras horizontales con la etiqueta del valor al final, en una sola tonalidad.
function Barras({ datos, total }) {
  const max = Math.max(...datos.map(d => d.monto), 1);
  return (
    <div style={{ padding: '10px 12px' }}>
      {datos.map((d, i) => (
        <div key={d._id || i} style={{ marginBottom: i === datos.length - 1 ? 0 : 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 3 }}>
            <span style={{ fontSize: 11.5, color: TINTA, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d._id}>{d._id || 'Sin dato'}</span>
            <span style={{ fontSize: 11, color: GRIS, flexShrink: 0 }}>{pct(d.monto, total).toFixed(1)}%</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <div style={{ flex: 1, height: 15, background: 'var(--well)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: `${Math.max((d.monto / max) * 100, 1.5)}%`, height: '100%', background: RAMPA[Math.min(i, RAMPA.length - 1)], borderRadius: '3px 4px 4px 3px' }} />
            </div>
            <span style={{ fontSize: 11.5, fontWeight: 700, color: TINTA, fontFamily: 'Geist Mono, monospace', flexShrink: 0, minWidth: 78, textAlign: 'right' }}>{pesos(d.monto)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// Tendencia mensual. Una sola serie, así que no lleva leyenda; se etiquetan sólo
// el primero, el último, el máximo y el mínimo — un número sobre cada punto
// convierte la gráfica en una tabla ilegible. El resto, al pasar el mouse.
function Tendencia({ serie }) {
  const [hover, setHover] = useState(null);
  const W = 700, H = 190, PL = 46, PR = 18, PT = 26, PB = 26;
  const max = Math.max(...serie.map(s => s.monto), 1);
  const pts = serie.map((s, i) => ({
    ...s,
    x: PL + (serie.length > 1 ? (i / (serie.length - 1)) * (W - PL - PR) : (W - PL - PR) / 2),
    y: PT + (1 - s.monto / max) * (H - PT - PB),
  }));
  const iMax = pts.reduce((a, p, i) => (p.monto > pts[a].monto ? i : a), 0);
  const iMin = pts.reduce((a, p, i) => (p.monto < pts[a].monto ? i : a), 0);
  const etiquetados = new Set([0, pts.length - 1, iMax, iMin]);
  const mesDe = (id) => { const [y, m] = id.split('-'); return `${MESES[parseInt(m, 10) - 1]} ${y.slice(2)}`; };
  const compacto = (n) => (Math.abs(n) >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : Math.abs(n) >= 1e3 ? `$${Math.round(n / 1e3)}K` : `$${Math.round(n)}`);

  if (!pts.length) return <div style={{ padding: 30, textAlign: 'center', fontSize: 12, color: GRIS }}>Sin gasto en los últimos 12 meses.</div>;

  return (
    <div style={{ padding: '6px 4px 2px' }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}
        onMouseLeave={() => setHover(null)}>
        {[0, 0.5, 1].map(f => {
          const y = PT + (1 - f) * (H - PT - PB);
          return (
            <g key={f}>
              <line x1={PL} y1={y} x2={W - PR} y2={y} stroke="#2a2e34" strokeWidth="1" />
              <text x={PL - 6} y={y + 3.5} textAnchor="end" style={{ fontSize: 9.5, fill: GRIS, fontFamily: 'Geist Mono, monospace' }}>{compacto(max * f)}</text>
            </g>
          );
        })}
        {pts.length > 1 && (
          <path d={`M ${pts.map(p => `${p.x} ${p.y}`).join(' L ')}`} fill="none" stroke={RAMPA[0]} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        )}
        {pts.map((p, i) => (
          <g key={p._id}>
            {/* Zona de hover más grande que el punto */}
            <rect x={p.x - 14} y={PT - 10} width="28" height={H - PT - PB + 20} fill="transparent" onMouseEnter={() => setHover(i)} style={{ cursor: 'default' }} />
            <circle cx={p.x} cy={p.y} r={hover === i ? 5.5 : 4.5} fill="#ffffff" stroke={RAMPA[0]} strokeWidth="2" />
            {(etiquetados.has(i) || hover === i) && (
              <text x={p.x} y={p.y - 11} textAnchor="middle" style={{ fontSize: 10, fontWeight: 700, fill: TINTA, fontFamily: 'Geist Mono, monospace' }}>
                {compacto(p.monto)}
              </text>
            )}
            <text x={p.x} y={H - PB + 15} textAnchor="middle" style={{ fontSize: 9.5, fill: hover === i ? TINTA : GRIS, fontWeight: hover === i ? 700 : 400 }}>
              {mesDe(p._id)}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

const Vacio = ({ children }) => (
  <div style={{ padding: '20px 14px', textAlign: 'center', fontSize: 12, color: GRIS }}>{children}</div>
);

// Segmentador: selector con encabezado marino, como los del tablero de BI. Se
// deshabilita solo cuando su catálogo viene vacío, en lugar de ofrecer una lista
// sin opciones.
function Segmentador({ etiqueta, valor, onChange, opciones, todas, etiquetar }) {
  const vacio = !opciones?.length;
  return (
    <div style={{ border: `1px solid ${BORDE}`, borderRadius: 8, overflow: 'hidden', background: 'var(--panel)', display: 'flex', flexDirection: 'column', minWidth: 150 }}>
      <div style={{ background: MARINO, color: '#fff', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', padding: '4px 10px' }}>{etiqueta}</div>
      <select value={valor} onChange={e => onChange(e.target.value)} disabled={vacio}
        title={vacio ? 'Sin opciones disponibles' : undefined}
        style={{ border: 'none', outline: 'none', flex: 1, padding: '6px 8px', fontSize: 12.5, color: valor ? TINTA : GRIS, background: 'var(--panel)', fontFamily: 'inherit', cursor: vacio ? 'not-allowed' : 'pointer', maxWidth: 200 }}>
        <option value="">{todas}</option>
        {(opciones || []).map(o => <option key={o} value={o}>{etiquetar ? etiquetar(o) : o}</option>)}
      </select>
    </div>
  );
}

// ── Tablero ────────────────────────────────────────────────────────────────

export default function DireccionGeneral({ onNavigate }) {
  const [periodo, setPeriodo] = useState('mes');
  const [custom, setCustom] = useState({ desde: '', hasta: '' });
  const [empresa, setEmpresa] = useState('');
  const [direccion, setDireccion] = useState('');
  const [categoria, setCategoria] = useState('');
  const [empresas, setEmpresas] = useState([]);
  // Las listas de dirección y categoría vienen en la respuesta y son catálogos
  // completos (no se recortan al filtrar), pero se guardan aparte para que no
  // parpadeen mientras carga la siguiente consulta.
  const [opciones, setOpciones] = useState({ direcciones: [], categorias: [] });
  const [data, setData] = useState(null);
  const [previo, setPrevio] = useState(null);
  const [tendencia, setTendencia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => { api.get('/empresas').then(l => setEmpresas(l.map(e => e.nombre))).catch(() => {}); }, []);

  const filtrosAPI = useMemo(() => ({
    ...(empresa ? { empresa } : {}),
    ...(direccion ? { direccion } : {}),
    ...(categoria ? { cuentaAgrupadora: categoria } : {}),
  }), [empresa, direccion, categoria]);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    const q = (r) => new URLSearchParams({ ...r, ...filtrosAPI }).toString();
    try {
      const [act, ant, tend] = await Promise.all([
        api.get(`/dashboard/analytics?${q(rangoPeriodo(periodo, custom))}`),
        api.get(`/dashboard/analytics?${q(rangoAnterior(periodo, custom))}`),
        api.get(`/dashboard/analytics?${q(rangoTendencia())}`),
      ]);
      setData(act);
      setPrevio(ant);
      setTendencia(tend.serie || []);
      setOpciones(o => ({
        direcciones: act.direcciones?.length ? act.direcciones : o.direcciones,
        categorias: act.categoriasGasto?.length ? act.categoriasGasto : o.categorias,
      }));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [periodo, custom, filtrosAPI]);

  useEffect(() => { cargar(); }, [cargar]);

  // Deja el filtro puesto para el tablero de finanzas y navega: quien quiere
  // profundizar no debería rearmar a mano lo que ya venía viendo.
  const irAlDetalle = (filtros) => {
    try { localStorage.setItem('dashboardFiltroInicial', JSON.stringify({ ...filtros, ...filtrosAPI, ...rangoPeriodo(periodo, custom) })); } catch {}
    if (onNavigate) onNavigate('dashboard');
  };

  const d = useMemo(() => {
    if (!data) return null;
    const porEstado = Object.fromEntries((data.porEstado || []).map(e => [e._id, e]));
    const de = (k) => porEstado[k] || { count: 0, monto: 0 };
    const proy = Object.fromEntries((data.proyeccionPago || []).map(p => [p.rango, p]));
    const total = data.totales?.monto || 0;
    const prev = data.totalesPrev?.monto || 0;
    const porPagar = data.backlogPorPagar || { count: 0, monto: 0 };
    const aprobada = data.backlogAprobada || { count: 0, monto: 0 };
    const viejos = (data.aging || []).filter(a => a.rango === '8-15' || a.rango === '15+');

    // Categorías que subieron contra el periodo anterior, de mayor a menor
    // incremento: es lo que hay que ir a revisar, no el ranking de siempre.
    const antPorCat = Object.fromEntries(((previo?.porCuentaAgrupadora) || []).map(c => [c._id, c.monto]));
    const incrementos = (data.porCuentaAgrupadora || [])
      .filter(c => c._id)
      .map(c => ({ _id: c._id, actual: c.monto, anterior: antPorCat[c._id] || 0, delta: c.monto - (antPorCat[c._id] || 0) }))
      .filter(c => c.delta > 0)
      .sort((a, b) => b.delta - a.delta)
      .slice(0, 6);

    return {
      total, prev,
      variacion: prev > 0 ? ((total - prev) / prev) * 100 : null,
      comprometido: porPagar.monto + aprobada.monto,
      comprometidoCount: porPagar.count + aprobada.count,
      porPagar, aprobada, proy,
      atrasado: proy.atrasado || { count: 0, monto: 0 },
      pendiente: de('pendiente'),
      viejos: {
        count: viejos.reduce((a, x) => a + x.count, 0),
        monto: viejos.reduce((a, x) => a + x.monto, 0),
        muyViejo: (data.aging || []).find(a => a.rango === '15+') || { count: 0, monto: 0 },
      },
      proveedores: (data.topProveedores || []).slice(0, 5),
      categorias: (data.porCuentaAgrupadora || []).filter(c => c._id).slice(0, 7),
      incrementos,
      ciclo: data.ciclo || {},
      count: data.totales?.count || 0,
    };
  }, [data, previo]);

  const hayFiltros = !!(empresa || direccion || categoria || periodo !== 'mes');
  const limpiarFiltros = () => {
    setEmpresa(''); setDireccion(''); setCategoria('');
    setPeriodo('mes'); setCustom({ desde: '', hasta: '' });
  };

  const CAJA_CONTROL = { border: `1px solid ${BORDE}`, borderRadius: 8, overflow: 'hidden', background: 'var(--panel)' };
  const ETIQUETA_CONTROL = { background: MARINO, color: '#fff', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', padding: '4px 10px' };

  const COMPROMISO = [
    { key: 'atrasado',       etiqueta: '🔴 Atrasado',             color: ROJO },
    { key: 'esta_semana',    etiqueta: '⏰ Esta semana',           color: AMBAR },
    { key: 'proxima_semana', etiqueta: '📅 Próxima semana',        color: GRIS },
    { key: 'este_mes',       etiqueta: '📆 Resto del mes',         color: GRIS },
    { key: 'mas_adelante',   etiqueta: '🗓️ Más adelante',          color: VERDE },
    { key: 'sin_fecha',      etiqueta: '❓ Programado sin fecha',  color: AMBAR },
  ];

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', maxWidth: 1320 }}>
      {/* ── Encabezado: título, cifra principal y sólo dos controles ── */}
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ background: MARINO, borderRadius: 8, padding: '12px 20px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ color: '#fff', fontSize: 17, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.15 }}>Dirección<br />General</div>
        </div>
        <div style={{ ...CAJA_CONTROL, background: 'var(--well)', padding: '10px 22px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minWidth: 210 }}>
          <div style={{ fontSize: 27, fontWeight: 800, color: 'var(--plasma)', fontFamily: 'Geist Mono, monospace', letterSpacing: '-0.02em' }}>
            {d ? pesos(d.total) : '—'}
          </div>
          <div style={{ fontSize: 10, fontWeight: 700, color: GRIS, textTransform: 'uppercase', letterSpacing: '0.09em', marginTop: 2 }}>Gasto neto del periodo</div>
          {d?.variacion != null && (
            <div style={{ fontSize: 11.5, fontWeight: 700, marginTop: 4, color: d.variacion >= 0 ? ROJO : VERDE }}>
              {d.variacion >= 0 ? '▲' : '▼'} {Math.abs(d.variacion).toFixed(1)}% vs. {pesos(d.prev)} anterior
            </div>
          )}
        </div>
        <Segmentador etiqueta="Compañía" valor={empresa} onChange={setEmpresa} opciones={empresas} todas="Todas" />
        <Segmentador etiqueta="Dirección" valor={direccion} onChange={setDireccion} opciones={opciones.direcciones}
          todas="Todas" etiquetar={sinPrefijoDireccion} />
        <Segmentador etiqueta="Tipo de gasto" valor={categoria} onChange={setCategoria} opciones={opciones.categorias} todas="Todos" />
        <div style={{ ...CAJA_CONTROL, display: 'flex', flexDirection: 'column' }}>
          <div style={ETIQUETA_CONTROL}>Periodo</div>
          <div style={{ display: 'flex', flex: 1, alignItems: 'stretch' }}>
            {PERIODOS.map((p, i) => (
              <button key={p.id} onClick={() => setPeriodo(p.id)}
                style={{ padding: '0 11px', border: 'none', borderLeft: i ? `1px solid ${BORDE}` : 'none',
                  background: periodo === p.id ? MARINO_2 : 'transparent', color: periodo === p.id ? '#fff' : GRIS,
                  fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
        {periodo === 'custom' && (
          <div style={{ ...CAJA_CONTROL, display: 'flex', flexDirection: 'column' }}>
            <div style={ETIQUETA_CONTROL}>Del / al</div>
            <div style={{ display: 'flex', flex: 1, alignItems: 'center', gap: 4, padding: '0 6px' }}>
              <input type="date" value={custom.desde} max={custom.hasta || undefined}
                onChange={e => setCustom(c => ({ ...c, desde: e.target.value }))}
                style={{ border: 'none', outline: 'none', fontSize: 12, color: TINTA, fontFamily: 'inherit' }} />
              <span style={{ color: GRIS, fontSize: 12 }}>–</span>
              <input type="date" value={custom.hasta} min={custom.desde || undefined}
                onChange={e => setCustom(c => ({ ...c, hasta: e.target.value }))}
                style={{ border: 'none', outline: 'none', fontSize: 12, color: TINTA, fontFamily: 'inherit' }} />
            </div>
          </div>
        )}
        {hayFiltros && (
          <button onClick={limpiarFiltros} title="Quitar todos los filtros"
            style={{ border: `1px solid ${BORDE}`, borderRadius: 8, background: 'var(--panel)', color: TINTA, cursor: 'pointer', fontSize: 11.5, fontWeight: 700, fontFamily: 'inherit', padding: '0 12px' }}>
            ✕ Limpiar
          </button>
        )}
        <div style={{ marginLeft: 'auto', alignSelf: 'center', fontSize: 11, color: GRIS, fontStyle: 'italic', textAlign: 'right', maxWidth: 210 }}>
          {[empresa || 'Todas las compañías', direccion && sinPrefijoDireccion(direccion), categoria].filter(Boolean).join(' · ')}<br />
          al {new Date().toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}
        </div>
      </div>

      {error && (
        <div style={{ border: `1.5px solid ${ROJO}`, borderRadius: 8, padding: '11px 14px', marginBottom: 14, color: ROJO, fontSize: 12.5, background: 'rgba(190,18,60,0.08)' }}>
          No se pudieron cargar las cifras: {error}
        </div>
      )}

      {loading || !d ? <Spinner /> : (
        <>
          {/* Lo urgente, con nombre propio. Cuando no hay nada atrasado también
              se dice: un tablero que sólo habla cuando hay problemas nadie sabe leerlo. */}
          <div style={{ border: `1.5px solid ${d.atrasado.count ? ROJO : VERDE}`, borderRadius: 8, background: d.atrasado.count ? 'rgba(190,18,60,0.08)' : 'rgba(21,128,61,0.07)', padding: '11px 15px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 16 }}>{d.atrasado.count ? '🔴' : '✅'}</span>
            <div style={{ flex: 1, minWidth: 250 }}>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: d.atrasado.count ? ROJO : VERDE }}>
                {d.atrasado.count
                  ? `${pesos(d.atrasado.monto)} en ${d.atrasado.count} pago${d.atrasado.count === 1 ? '' : 's'} atrasado${d.atrasado.count === 1 ? '' : 's'}`
                  : 'Sin pagos atrasados'}
              </span>
              <div style={{ fontSize: 11.5, color: GRIS, marginTop: 2 }}>
                {d.atrasado.count
                  ? 'Ya pasó su fecha programada de pago: es lo primero que hay que liberar o reprogramar.'
                  : 'Todo lo programado está dentro de su fecha.'}
              </div>
            </div>
            {d.atrasado.count > 0 && (
              <button onClick={() => irAlDetalle({ estado: 'por_pagar' })}
                style={{ padding: '5px 12px', borderRadius: 5, border: `1.5px solid ${ROJO}`, background: 'var(--panel)', color: ROJO, cursor: 'pointer', fontSize: 11.5, fontWeight: 700, fontFamily: 'inherit' }}>
                Ver los atrasados →
              </button>
            )}
          </div>

          {/* ── Cifras que abren la conversación ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 215px), 1fr))', gap: 12, marginBottom: 16 }}>
            <Cifra etiqueta="Comprometido sin pagar" valor={pesos(d.comprometido)}
              sub={`${d.comprometidoCount} solicitudes ya autorizadas`}
              nota={d.atrasado.count ? { icono: '🔴', texto: `${pesos(d.atrasado.monto)} atrasado`, color: ROJO } : null}
              acento={MARINO_2} />
            <Cifra etiqueta="Esperando autorización" valor={pesos(d.pendiente.monto)}
              sub={`${d.pendiente.count} solicitudes en validación`}
              nota={d.viejos.count ? { icono: '⚠', texto: `${d.viejos.count} con más de 8 días`, color: AMBAR } : null}
              acento={AMBAR} />
            <Cifra etiqueta="De la solicitud al pago"
              valor={d.ciclo.totalDias != null ? `${d.ciclo.totalDias} días` : '—'}
              sub={d.ciclo.muestras ? `promedio de ${d.ciclo.muestras} pagos` : 'sin pagos en el periodo'}
              nota={d.ciclo.aprobacionDias != null ? { icono: '·', texto: `${d.ciclo.aprobacionDias} días en autorizarse`, color: GRIS } : null}
              acento={MARINO_2} />
          </div>

          {/* ── Fila 1: compromiso de caja · categorías que subieron ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 430px), 1fr))', gap: 14, marginBottom: 14 }}>
            <Panel titulo="Compromiso de caja" decision="¿Qué se libera esta semana y qué aguanta?"
              accion={<BotonDetalle onClick={() => irAlDetalle({ estado: 'por_pagar' })} />}>
              {!d.porPagar.count && !d.aprobada.count ? <Vacio>No hay pagos comprometidos pendientes de salir.</Vacio> : (
                <Tabla
                  columnas={[{ label: 'Cuándo debe salir' }, { label: 'Sol.', num: true }, { label: 'Importe', num: true }, { label: '%', num: true }]}
                  filas={[
                    ...COMPROMISO.map(({ key, etiqueta }) => {
                      const b = d.proy[key];
                      if (!b?.count) return null;
                      return { key, celdas: [etiqueta, b.count, pesos(b.monto), `${pct(b.monto, d.porPagar.monto).toFixed(1)}%`] };
                    }).filter(Boolean),
                    // Lo aprobado sin programar no tiene fecha y es lo que más se
                    // atora: sin fecha de salida, nadie lo está esperando.
                    ...(d.aprobada.count ? [{ key: 'aprobada', celdas: ['⏳ Aprobado, aún sin programar', d.aprobada.count, pesos(d.aprobada.monto), '—'] }] : []),
                  ]}
                  total={['Total comprometido', d.comprometidoCount, pesos(d.comprometido), '']}
                />
              )}
            </Panel>

            <Panel titulo="Categorías que más subieron" decision="¿Qué se nos disparó contra el periodo anterior?">
              {d.incrementos.length === 0 ? <Vacio>Ninguna categoría subió respecto al periodo anterior.</Vacio> : (
                <Tabla
                  columnas={[{ label: 'Categoría' }, { label: 'Anterior', num: true }, { label: 'Actual', num: true }, { label: 'Incremento', num: true }]}
                  filas={d.incrementos.map(c => ({
                    key: c._id,
                    celdas: [c._id, pesos(c.anterior), pesos(c.actual),
                      <span style={{ color: ROJO, fontWeight: 700 }}>{pesos(c.delta)} ▲</span>],
                  }))}
                  total={['Total del incremento', '', '',
                    <span style={{ color: ROJO }}>{pesos(d.incrementos.reduce((a, c) => a + c.delta, 0))} ▲</span>]}
                />
              )}
            </Panel>
          </div>

          {/* ── Fila 2: proveedores · categorías ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))', gap: 14, marginBottom: 14 }}>
            <Panel titulo="Principales proveedores" decision="¿Con quién conviene negociar?">
              {d.proveedores.length === 0 ? <Vacio>Sin gasto en el periodo.</Vacio> : <Barras datos={d.proveedores} total={d.total} />}
            </Panel>

            <Panel titulo="Categorías del gasto" decision="¿En qué se nos va el dinero?">
              {d.categorias.length === 0 ? <Vacio>Sin categorías clasificadas en el periodo.</Vacio> : (
                <Tabla
                  columnas={[{ label: 'Nombre del gasto' }, { label: 'Importe', num: true }, { label: '%', num: true }]}
                  filas={d.categorias.map(c => ({ key: c._id, celdas: [c._id, pesos(c.monto), `${pct(c.monto, d.total).toFixed(2)}%`] }))}
                  total={['Total', pesos(d.total), '100%']}
                />
              )}
            </Panel>

          </div>

          {/* ── Fila 3: tendencia ── */}
          <Panel titulo="Tendencia" decision="¿Vamos a la baja o se nos está yendo la mano? Últimos 12 meses.">
            <Tendencia serie={tendencia} />
          </Panel>

          <div style={{ fontSize: 11, color: GRIS, marginTop: 10, lineHeight: 1.6, fontStyle: 'italic' }}>
            Importes netos (con IVA y menos retenciones) y en pesos: lo que está en otra moneda se convierte con el
            tipo de cambio del pago, o con el de referencia si todavía no se paga. Son las mismas cifras del tablero
            de finanzas, ordenadas por lo que hay que decidir.
          </div>
        </>
      )}
    </div>
  );
}
